import cx from "classnames";
import { QuantitySelector } from "components/cart/item-children/QuantitySelector";
import { CylinderCompatibilityDialog } from "components/GasExchange/CylinderCompatibilityDialog";
import { AlertInfoIconSvg } from "components/icons/Alert/AlertInfoIconSvg";
import { NewButton } from "components/NewButton/NewButton";
import { Spinner } from "components/spinner/Spinner";
import { cart as cartConfig } from "config";
import { AppContext } from "context/app";
import { CartContext } from "context/cart";
import { LocalizationContext } from "context/localization";
import { useTranslationContext } from "context/translation";
import {
  getEcommerceWithValue,
  getEventProduct,
  logProductDetailViewEvent,
} from "helpers/analyticsLogger";
import { getProductPrice, isProductOutOfStock } from "helpers/product";
import { getYotpoInstanceId } from "helpers/yotpo";
import { useCartAnalyticsEvents } from "hooks/analytics/useCartAnalyticsEvents";
import { useScreenSize } from "hooks/useScreenSize";
import { useRouter } from "next/router";
import { FC, useContext, useEffect, useState } from "react";
import { StructuredText } from "react-datocms";
import { Image } from "react-datocms/image";
import {
  ProductBySlugQuery,
  ResponsiveImage,
} from "services/datocms/generated";
import {
  CartFragment,
  ItemByGtinQuery,
  useDynamicBundleByTagQuery,
} from "services/graphql/generated";
import { ProductNutritionInformation } from "./ProductNutritionInformation";

interface ProductDetailsDefaultProps {
  datoProduct: ProductBySlugQuery["product"] | null;
  selectedImage: number;
  setSelectedImage: (index: number) => void;
  pepDirectProduct?: ItemByGtinQuery["itemByGtin"];
  loading?: boolean;
}

export const ProductDetailsDefault: FC<ProductDetailsDefaultProps> = ({
  datoProduct,
  selectedImage,
  setSelectedImage,
  pepDirectProduct,
  loading = false,
}) => {
  const { isMobile } = useScreenSize();
  const [quantity, setQuantity] = useState(1);
  const [cartLoading, setCartLoading] = useState(false);

  const [showCompatilityModal, setShowCompatilityModal] = useState(false);
  const { formatPriceByLocale } = useContext(LocalizationContext);
  const {
    addToCart,
    comingSoon,
    doesBottleFitMachine,
    howManyDoYouNeed,
    bottleCompatibleWithTheFollowing,
  } = useTranslationContext();
  const { tenant } = useContext(AppContext);
  const currency = tenant?.checkout?.paymentOptions?.defaultCurrencyCode;
  const router = useRouter();
  const { updateCartItem, updateCartItemWithBundle } = useContext(CartContext);
  const { addToCartEvent } = useCartAnalyticsEvents();
  const { maxCartQty: maxCartQtyDefault } = cartConfig;
  const maxCartQty =
    tenant?.shoppingBagConfig?.maxAmountForSelect || maxCartQtyDefault;

  const { data: bundle, loading: bundleLoading } = useDynamicBundleByTagQuery({
    skip: !datoProduct?.bundleTag,
    variables: {
      tag: datoProduct?.bundleTag ?? "",
    },
  });

  const pepDirectBundle = bundle?.dynamicBundleByTag;

  useEffect(() => {
    if (pepDirectProduct && datoProduct && currency) {
      if (datoProduct.bundleTag && bundle?.dynamicBundleByTag) {
        const eventProduct = getEventProduct(
          pepDirectProduct,
          bundle?.dynamicBundleByTag,
          {
            currency,
            quantity: 1,
            itemCategory: datoProduct?.category?.analyticsCategoryName ?? "",
          },
        );
        logProductDetailViewEvent(currency, [eventProduct], eventProduct.price);
      } else {
        const eventProduct = getEventProduct(pepDirectProduct, undefined, {
          currency,
          quantity: 1,
          itemCategory: datoProduct?.category?.analyticsCategoryName ?? "",
        });
        logProductDetailViewEvent(currency, [eventProduct], eventProduct.price);
      }
    }
  }, [pepDirectProduct, datoProduct, currency, bundle]);

  const handleAddItemsToCart = async (quantity: number = 1) => {
    setCartLoading(true);
    const analyticsDetails = getEcommerceWithValue({
      currency,
      itemListName: undefined,
      items: [
        getEventProduct(pepDirectProduct, pepDirectBundle, {
          itemCategory: datoProduct?.category?.analyticsCategoryName ?? "",
          currency,
          quantity,
        }),
      ],
    });
    let newCart: CartFragment | null = null;
    if (pepDirectBundle) {
      newCart = await updateCartItemWithBundle({
        bundleTag: pepDirectBundle.tag,
        quantity,
        analyticsDetails,
      });
    } else {
      newCart = await updateCartItem({
        productId: pepDirectProduct?.id,
        quantity,
        subscriptionIntervalInDays: null,
        analyticsDetails,
        includeExistingQuantity: true,
      });
    }
    await addToCartEvent(newCart, analyticsDetails);
    router.push("/cart");

    const handleRouteChangeComplete = () => {
      setCartLoading(false);
      router.events.off("routeChangeComplete", handleRouteChangeComplete);
    };
    router.events.on("routeChangeComplete", handleRouteChangeComplete);
  };

  const formattedPrice = formatPriceByLocale(
    getProductPrice(pepDirectProduct, pepDirectBundle),
  );
  const hasCompatibleMachines =
    datoProduct?.compatibleMachines &&
    datoProduct?.compatibleMachines.length > 0;

  const outOfStock =
    !loading && isProductOutOfStock(pepDirectProduct, pepDirectBundle);
  const addToCartButtonText = loading
    ? ""
    : outOfStock
      ? comingSoon
      : addToCart;

  return (
    <div className="container md:py-20 flex flex-col md:flex-row gap-6">
      <div className="flex flex-col md:w-6/12">
        <div className="max-w-[285px] lg:max-w-[458px] self-center">
          {datoProduct?.productImages?.[selectedImage].responsiveImage && (
            <Image
              data={
                datoProduct?.productImages?.[selectedImage]
                  .responsiveImage as ResponsiveImage
              }
            />
          )}
        </div>
        {datoProduct?.productImages &&
          datoProduct?.productImages.length > 1 && (
            <div className="w-full overflow-x-scroll flex items-center gap-2 mt-4 p-1">
              {datoProduct?.productImages.map((image, index) => (
                <div
                  key={image.responsiveImage?.src}
                  onClick={() => setSelectedImage(index)}
                  className={cx(
                    "w-[100px] relative cursor-pointer",
                    index === selectedImage &&
                      "outline outline-2 outline-secondary",
                  )}
                >
                  <Image
                    objectFit="cover"
                    layout="responsive"
                    data={image.responsiveImage as ResponsiveImage}
                  />
                </div>
              ))}
            </div>
          )}
      </div>
      <div className="flex flex-col gap-10 md:w-6/12">
        <>
          <div className="flex flex-col gap-2">
            <h1 className="text-blue-950 text-4xl lg:text-5xl font-semibold self-stretch leading-normal lg:leading-normal">
              {datoProduct?.title}
            </h1>
            {loading ? (
              <div className="rounded-md w-[180px] h-[32px] bg-gray-200 animate-pulse-opacity" />
            ) : (
              <p className="text-2xl lg:text-[32px] text-black font-semibold">
                {formattedPrice}
              </p>
            )}
            {pepDirectProduct?.gtin && (
              <div
                className="yotpo-widget-instance"
                data-yotpo-instance-id={getYotpoInstanceId("stars")}
                data-yotpo-product-id={pepDirectProduct?.gtin}
              />
            )}
            {datoProduct?.lowestPriceText && (
              <div className="flex flex-col mt-4 text-gray-500 font-poppins text-xs font-medium leading-normal">
                <p>{datoProduct?.lowestPriceText}</p>
              </div>
            )}
          </div>
          {!datoProduct?.bundleTag && (
            <QuantitySelector
              allowRemoveButton={false}
              disabled={loading}
              maxQty={maxCartQty}
              outOfStock={outOfStock}
              quantity={quantity}
              title={howManyDoYouNeed}
              updateQty={async (qty) => setQuantity(qty)}
            />
          )}
          {hasCompatibleMachines && (
            <div className="flex gap-1 items-center">
              <AlertInfoIconSvg className="fill-sodaStreamBlue" />
              <p
                className="text-sodaStreamBlue text-sm font-medium cursor-pointer"
                onClick={() => setShowCompatilityModal(true)}
              >
                {doesBottleFitMachine}
              </p>
            </div>
          )}
          <div className="bg-white max-sm:fixed bottom-0 max-sm:px-4 max-sm:py-2 left-0 right-0 w-full z-50">
            <NewButton
              bi-id={pepDirectProduct?.title}
              bi-type={`${pepDirectProduct?.title} pdp - Add to cart`}
              disabled={outOfStock || loading || cartLoading || bundleLoading}
              fullWidth={isMobile}
              onClick={() => handleAddItemsToCart(quantity)}
            >
              {cartLoading ? (
                <Spinner
                  color="white"
                  dotSize="10px"
                  className="min-w-[50px] w-full"
                />
              ) : (
                addToCartButtonText
              )}
            </NewButton>
          </div>
          <div className="text-gray-900 text-[15px] font-medium leading-normal">
            <StructuredText data={datoProduct?.productContent?.value} />
          </div>
          {datoProduct?.nutrition && (
            <ProductNutritionInformation
              productTitle={datoProduct.title ?? ""}
              nutritionData={datoProduct?.nutrition}
            />
          )}
        </>
      </div>
      {showCompatilityModal && hasCompatibleMachines && (
        <CylinderCompatibilityDialog
          machines={datoProduct?.compatibleMachines}
          header={bottleCompatibleWithTheFollowing}
          onClose={() => setShowCompatilityModal(false)}
          onOkClick={() => setShowCompatilityModal(false)}
        />
      )}
    </div>
  );
};
